import React from 'react';
import { Mail, PauseCircle, Phone } from 'lucide-react';
import PortalService from '../../../services/portal/portal.service';
import { usePortalResource } from '../usePortalResource';
import PortalSection, { Card, EmptyState } from '../PortalSection';
import PortalAvatar from '../PortalAvatar';
import { FOCUS_RING } from '../format';
import VisitSlotCard from './VisitSlotCard';

interface HomeScreenProps {
  patientId: string;
  patientName: string;
  patientFullName?: string;
  patientPictureUrl?: string | null;
}

/** Home: patient banner, next visit (or "Visits are paused", EX-24) and the agency's contact details. */
const HomeScreen: React.FC<HomeScreenProps> = ({ patientId, patientName, patientFullName, patientPictureUrl = null }) => {
  const { data, loading, error } = usePortalResource(PortalService.getHome, patientId);
  const { data: contact } = usePortalResource(PortalService.getContact, patientId);
  const linkClass = `inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm text-slate-900 hover:bg-slate-50 ${FOCUS_RING}`;
  return (
    <PortalSection title={`Home${patientName ? ` · ${patientName}` : ''}`} loading={loading} error={error}>
      {data && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-4 rounded-2xl bg-gradient-to-r from-slate-900 to-teal-800 p-5 text-white shadow-sm">
            <PortalAvatar name={patientFullName || patientName} pictureUrl={patientPictureUrl} size="xl" tone="dark" />
            <div className="min-w-0">
              <p className="truncate text-xl font-semibold">{patientFullName || patientName}</p>
              <p className="mt-1 text-sm text-teal-100">
                {data.paused ? 'Visits are currently on hold' : 'Care is in progress. Here is what is coming up.'}
              </p>
            </div>
          </div>

          {data.paused && (
            <div role="status" className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <PauseCircle size={20} aria-hidden="true" />
              <p className="font-medium">{data.pausedMessage || 'Visits are paused'}</p>
            </div>
          )}

          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <h2 className="mb-3 text-base font-semibold text-slate-900">Next visit</h2>
              {data.nextVisit ? (
                <VisitSlotCard slot={data.nextVisit} />
              ) : (
                <EmptyState>No upcoming visits are scheduled.</EmptyState>
              )}
            </Card>

            {contact && (contact.agencyName || contact.phone || contact.email) && (
              <Card>
                <h2 className="text-base font-semibold text-slate-900">Your care agency</h2>
                {contact.agencyName && <p className="mt-1 text-slate-800">{contact.agencyName}</p>}
                {contact.officeHours && <p className="text-sm text-slate-700">Office hours: {contact.officeHours}</p>}
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className={linkClass}>
                      <Phone size={16} aria-hidden="true" />
                      <span>{contact.phone}</span>
                    </a>
                  )}
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className={linkClass}>
                      <Mail size={16} aria-hidden="true" />
                      <span className="truncate">{contact.email}</span>
                    </a>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </PortalSection>
  );
};

export default HomeScreen;
